import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import { ExternalLink } from 'lucide-react'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import {
  getSystemSettings,
  updateSystemSettings,
} from '../../gen/cortado/v1/admin-AdminService_connectquery'
import { adminListUsers } from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetSystemSettingsAdminRequestSchema,
  SystemSettingsSchema,
  AdminListUsersRequestSchema,
} from '../../gen/cortado/v1/admin_pb'
import { AgentModelProviderEnum } from '../../gen/cortado/v1/settings_pb'
import { Switch } from '../../components/ui/switch'
import { Label } from '../../components/ui/label'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'

export const Route = createFileRoute('/_admin/settings')({
  component: AdminSettings,
})

const modelDirectoryLinks = {
  anthropic: {
    label: 'Claude model IDs',
    href: 'https://platform.claude.com/docs/en/about-claude/models/overview',
  },
  xai: {
    label: 'Grok model IDs',
    href: 'https://docs.x.ai/developers/models',
  },
} as const

function timeAgo(timestamp?: { seconds: bigint }) {
  if (!timestamp) return '-'
  const now = Date.now()
  const then = Number(timestamp.seconds) * 1000
  const diff = now - then

  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`

  return new Date(then).toLocaleDateString()
}

function AdminSettings() {
  const queryClient = useQueryClient()
  const [optimistic, setOptimistic] = useState<boolean | null>(null)
  const [draftProvider, setDraftProvider] = useState<'anthropic' | 'xai' | null>(null)
  const [draftModel, setDraftModel] = useState<string | null>(null)
  const [draftAddress, setDraftAddress] = useState<string | null>(null)

  const settingsQuery = useQuery(
    getSystemSettings,
    create(GetSystemSettingsAdminRequestSchema, {}),
  )

  const usersQuery = useQuery(
    adminListUsers,
    create(AdminListUsersRequestSchema, { pageSize: 100 }),
  )

  const userEmailMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of usersQuery.data?.users ?? []) {
      map.set(user.id, user.email)
    }
    return map
  }, [usersQuery.data?.users])

  const mutation = useMutation(updateSystemSettings, {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getSystemSettings,
          cardinality: undefined,
        }),
      })
      setOptimistic(null)
      if (variables.updateMask?.paths?.includes('postal_address')) {
        setDraftAddress(null)
        toast.success('Postal address updated')
      } else if (variables.updateMask?.paths?.includes('agent_model')) {
        setDraftProvider(null)
        setDraftModel(null)
        toast.success('AI model updated')
      } else {
        const nowEnabled = data.settings?.signupEnabled
        toast.success(nowEnabled ? 'Signups enabled' : 'Signups disabled')
      }
    },
    onError: (err: Error) => {
      setOptimistic(null)
      toast.error(`Failed to update settings: ${err.message}`)
    },
  })

  const settings = settingsQuery.data?.settings
  const hasLoaded = settingsQuery.isSuccess || !!settingsQuery.data

  if (settingsQuery.isLoading || !hasLoaded) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground text-sm">
            Platform-wide configuration
          </p>
        </div>
        <p className="text-sm text-muted-foreground">Loading settings...</p>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>
        <p className="text-sm text-muted-foreground">Settings unavailable.</p>
      </div>
    )
  }

  const signupEnabled = optimistic ?? settings.signupEnabled
  const persistedProvider = settings.agentModelProvider === AgentModelProviderEnum.XAI
    ? 'xai'
    : 'anthropic'
  const provider = draftProvider ?? persistedProvider
  const model = draftModel ?? settings.agentModel
  const modelChanged = provider !== persistedProvider || model.trim() !== settings.agentModel
  const postalAddress = draftAddress ?? settings.postalAddress
  const addressChanged = postalAddress.trim() !== settings.postalAddress.trim()
  const updatedByEmail = settings.updatedByUserId
    ? userEmailMap.get(settings.updatedByUserId) || settings.updatedByUserId
    : null

  const handleToggle = (checked: boolean) => {
    setOptimistic(checked)
    mutation.mutate({
      settings: create(SystemSettingsSchema, { signupEnabled: checked }),
      updateMask: create(FieldMaskSchema, { paths: ['signup_enabled'] }),
    })
  }

  const handleProviderChange = (value: 'anthropic' | 'xai') => {
    const defaultModels = {
      anthropic: 'claude-opus-5',
      xai: 'grok-4.6',
    }
    setDraftProvider(value)
    if (!model.trim() || model === defaultModels[provider]) {
      setDraftModel(defaultModels[value])
    }
  }

  const handleSaveAddress = () => {
    const address = postalAddress.trim()
    if (!address) {
      toast.error('Postal address is required')
      return
    }
    mutation.mutate({
      settings: create(SystemSettingsSchema, { postalAddress: address }),
      updateMask: create(FieldMaskSchema, { paths: ['postal_address'] }),
    })
  }

  const handleSaveModel = () => {
    mutation.mutate({
      settings: create(SystemSettingsSchema, {
        agentModelProvider: provider === 'xai'
          ? AgentModelProviderEnum.XAI
          : AgentModelProviderEnum.ANTHROPIC,
        agentModel: model.trim(),
      }),
      updateMask: create(FieldMaskSchema, {
        paths: ['agent_model_provider', 'agent_model'],
      }),
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground text-sm">
          Platform-wide configuration
        </p>
      </div>

      <div className="max-w-2xl rounded-lg border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="signup-enabled" className="text-base font-medium">
              Enable signups
            </Label>
            <p className="text-sm text-muted-foreground">
              When disabled, new users cannot create accounts. Existing users
              and invite-based signups are unaffected.
            </p>
          </div>
          <Switch
            id="signup-enabled"
            checked={signupEnabled}
            onCheckedChange={handleToggle}
            disabled={mutation.isPending}
          />
        </div>

        {(settings.updatedAt || updatedByEmail) && (
          <p className="mt-6 border-t pt-4 text-xs text-muted-foreground">
            Last updated {timeAgo(settings.updatedAt)}
            {updatedByEmail ? ` by ${updatedByEmail}` : ''}
          </p>
        )}
      </div>

      <div className="max-w-2xl space-y-5 rounded-lg border p-4 sm:p-6">
        <div className="space-y-1">
          <h2 className="text-base font-medium">Postal address</h2>
          <p className="text-sm text-muted-foreground">
            Shown at the bottom of marketing email. A physical street address is required
            before a campaign can be sent.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="postal-address">Address</Label>
          <Input
            id="postal-address"
            value={postalAddress}
            onChange={(event) => setDraftAddress(event.target.value)}
            placeholder="Street, city, state, ZIP"
            maxLength={300}
            disabled={mutation.isPending}
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSaveAddress}
            disabled={!addressChanged || !postalAddress.trim() || mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save postal address'}
          </Button>
        </div>
      </div>

      <div className="max-w-2xl space-y-5 rounded-lg border p-4 sm:p-6">
        <div className="space-y-1">
          <h2 className="text-base font-medium">AI chat model</h2>
          <p className="text-sm text-muted-foreground">
            Choose the provider and exact model identifier used for new AI chat turns.
            xAI requires XAI_API_KEY to be configured on the backend.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
          <div className="space-y-2">
            <Label htmlFor="agent-model-provider">Provider</Label>
            <Select
              value={provider}
              onValueChange={(value) => handleProviderChange(value as 'anthropic' | 'xai')}
              disabled={mutation.isPending}
            >
              <SelectTrigger id="agent-model-provider" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="anthropic">Anthropic</SelectItem>
                <SelectItem value="xai">xAI</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="agent-model">Model</Label>
              <a
                href={modelDirectoryLinks[provider].href}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs underline-offset-4 hover:underline"
              >
                {modelDirectoryLinks[provider].label}
                <ExternalLink aria-hidden="true" className="h-3 w-3" />
              </a>
            </div>
            <Input
              id="agent-model"
              value={model}
              onChange={(event) => setDraftModel(event.target.value)}
              placeholder={provider === 'xai' ? 'grok-4.6' : 'claude-opus-5'}
              maxLength={255}
              disabled={mutation.isPending}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSaveModel}
            disabled={!modelChanged || !model.trim() || mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save AI model'}
          </Button>
        </div>
      </div>
    </div>
  )
}
